import {ElementRef} from '@angular/core';
import {CompetitionModel} from '../../../models/competition/competition.model';
import {CompetitionService} from '../../../services/competition/competition.service';
import {LiveRamp} from './live-ramp';

/**
 * Keeps the totals of a live competition up to date for a counter on screen. Polling only runs while the
 * competition is public and live and the counter is in view, to spare the backend. Timers keep firing in a
 * background tab, though browsers may slow them down. Stops for good once the server says the competition
 * is not live.
 * The totals are eased towards each polled value by a LiveRamp so the counter keeps moving between polls.
 */
export class LiveCompetitionCounts {

  /** How often the live totals are refreshed while the competition is running. */
  private static INTERVAL = 15 * 1000;
  private static TICK_INTERVAL = LiveCompetitionCounts.INTERVAL / LiveRamp.TICKS;

  private comp: CompetitionModel | null = null;
  /** True when the competition is public and running, and the server has not said otherwise. */
  private eligible = false;
  /** True while the counter is on screen. */
  private inView = false;
  private active = false;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private observer: IntersectionObserver | null = null;
  private tickId: ReturnType<typeof setInterval> | null = null;
  private answersRamp = new LiveRamp();
  private correctRamp = new LiveRamp();

  constructor(private compSv: CompetitionService) {
  }

  /**
   * The competition to keep up to date, null when there is none.
   */
  setCompetition(comp: CompetitionModel | null) {

    this.stopPolling();
    this.comp = comp;
    if (comp) {
      this.answersRamp.reset(comp.numAnswers);
      this.correctRamp.reset(comp.numCorrect);
    }
    this.eligible = !!comp && comp.isPublic && comp.isActive();
    this.sync();
  }

  /**
   * The counter element, null when it is not rendered. Its visibility starts and stops the polling.
   */
  setCounter(element: ElementRef | null) {

    this.disconnect();

    if (!element) {
      this.inView = false;
      this.sync();
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      this.inView = true;
      this.sync();
      return;
    }

    // Visibility is about scroll position, so a background tab keeps counting as in view
    this.observer = new IntersectionObserver((entries) => {
      this.inView = entries[entries.length - 1].isIntersecting;
      this.sync();
    });
    this.observer.observe(element.nativeElement);
  }

  destroy() {

    this.eligible = false;
    this.stopPolling();
    this.disconnect();
  }

  private disconnect() {

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private sync() {

    const shouldPoll = this.eligible && this.inView && !!this.comp;

    if (shouldPoll && !this.active) {
      this.active = true;
      // The counter was out of view so the totals may be stale, refresh right away
      this.schedule(this.comp._id, 0);
      return;
    }

    if (!shouldPoll && this.active) {
      this.stopPolling();
    }
  }

  private schedule(compId: string, delay: number) {

    this.timeoutId = setTimeout(() => {

      this.compSv.fetchPublicLiveCounts(compId).subscribe((counts) => {

        if (!this.active || !this.comp || this.comp._id !== compId) {
          return;
        }

        this.applyCounts(counts);
        this.schedule(compId, LiveCompetitionCounts.INTERVAL);

      }, (error) => {

        if (!this.active || !this.comp || this.comp._id !== compId) {
          return;
        }

        // A network hiccup is retried, a competition that is no longer live is not
        if (error && error.name === 'CompetitionNotLive') {
          this.eligible = false;
          this.stopPolling();
          return;
        }

        this.schedule(compId, LiveCompetitionCounts.INTERVAL);
      });

    }, delay);
  }

  private applyCounts(counts: { numCorrect: number, numAnswers: number }) {

    this.answersRamp.update(counts.numAnswers);
    this.correctRamp.update(counts.numCorrect);
    this.comp.numAnswers = this.answersRamp.value;
    this.comp.numCorrect = this.correctRamp.value;
    this.startTicking();
  }

  private startTicking() {

    this.stopTicking();
    this.tickId = setInterval(() => {

      if (!this.comp) {
        return;
      }

      this.answersRamp.tick();
      this.correctRamp.tick();
      this.comp.numAnswers = this.answersRamp.value;
      this.comp.numCorrect = this.correctRamp.value;

    }, LiveCompetitionCounts.TICK_INTERVAL);
  }

  private stopTicking() {

    if (this.tickId) {
      clearInterval(this.tickId);
      this.tickId = null;
    }
  }

  private stopPolling() {

    this.stopTicking();
    this.active = false;
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

}
